import xhr from 'xhr';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import Month from './month';
import { fetchEnrollment, fetchEnrollmentById, saveMonths } from './useMonthStudent';

/**
 * Modal para actualizar los meses pagados de una matrícula (desde el reporte de pagos).
 * URL: ?open=payment-months&programType=<id>&program=<id>&enrollment=<id>&semester=<id>
 *   - Si viene `enrollment` en la URL, se carga esa matrícula directamente.
 *   - Si no, se resuelve por (student, program, programType) — fallback para deep-links antiguos.
 */
export default function UpdateMonthsModal({
  isOpen,
  onClose,
  studentId,
  programTypeId,
  programId,
  programTypes,
  enrollmentId,        // NUEVO: del deep-link
  preselectedSemesterId, // NUEVO: del deep-link
}) {
  const [enrollment, setEnrollment] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [monthState, setMonthState] = useState(null);
  const [updating, setUpdating] = useState(false);
  const toast = useToast();

  const programType = programTypes?.find((p) => Number(p.id) === Number(programTypeId));
  const type = programType?.type;

  const resolveEnrollment = useCallback(async () => {
    if (!type || type === 'course') return;
    setFetching(true);
    setEnrollment(null);
    try {
      if (enrollmentId) {
        const enr = await fetchEnrollmentById(type, enrollmentId);
        setEnrollment(enr || null);
      } else if (studentId && programTypeId && programId) {
        const enr = await fetchEnrollment(type, studentId, programId, programTypeId);
        setEnrollment(enr || null);
      }
    } catch (err) {
      console.error('UpdateMonthsModal resolve enrollment:', err);
      toast({ title: 'No se pudo cargar el programa.', status: 'error', position: 'bottom' });
      setEnrollment(null);
    } finally {
      setFetching(false);
    }
  }, [enrollmentId, studentId, programTypeId, programId, type, toast]);

  useEffect(() => {
    if (isOpen && type && (enrollmentId || (studentId && programTypeId && programId))) {
      resolveEnrollment();
    }
  }, [isOpen, enrollmentId, studentId, programTypeId, programId, type, resolveEnrollment]);

  const handleUpdateMonths = useCallback(async () => {
    if (!monthState?.enrollmentId || !monthState?.enrollmentType) return;
    if (monthState.isSemesterBased && !monthState.semesterId) {
      toast({ title: 'Selecciona un semestre antes de guardar', status: 'warning', position: 'bottom' });
      return;
    }
    setUpdating(true);
    try {
      await saveMonths({
        studentId,
        enrollmentType: monthState.enrollmentType,
        enrollmentId: monthState.enrollmentId,
        // No semester-based → null (misma regla que Month.handleSave / form.js).
        semesterId: monthState.semesterId ?? null,
        months: monthState.months,
        on_day: monthState.onDay,
        promoteOrphan: !!monthState.promoteOrphan,
      });
      // "Al día en derechos de grado" (solo si el tipo de programa lo tiene activo): vive en el enrollment.
      // Month solo lo emite cuando el program-type tiene has_graduation_fee.
      if (monthState.graduationFeePaid !== undefined) {
        await xhr.put(`/${monthState.enrollmentType.replace('_', '-')}-enrollments/${monthState.enrollmentId}`, {
          data: { graduation_fee_paid: !!monthState.graduationFeePaid },
        });
      }
      toast({ title: 'Los meses se actualizaron correctamente!', status: 'success', position: 'bottom' });
      // No cerramos el modal al guardar — solo se cierra cuando el usuario presiona la X.
    } catch (err) {
      console.error('UpdateMonthsModal save:', err);
      toast({ title: 'No se pudieron actualizar los meses.', status: 'error', position: 'bottom' });
    } finally {
      setUpdating(false);
    }
  }, [monthState, studentId, toast]);

  const handleClose = () => {
    setEnrollment(null);
    setMonthState(null);
    onClose();
  };

  if (!type) return null;

  const programLabel = programType?.name ?? 'Programa';
  const programName = enrollment?.[type]?.name ?? '';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="560px">
        <ModalHeader pb={2}>
          <Text fontSize="lg" fontWeight="600">Actualizar meses pagados</Text>
          <Text fontSize="sm" color="gray.600" fontWeight="normal" mt={1}>
            {programLabel}{programName ? ` — ${programName}` : ''}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Este estudiante tiene pagos registrados pero no se indicaron los meses. Selecciona los meses que cubre
            el pago y guarda.
          </Text>

          {fetching ? (
            <Flex justify="center" py={8}><Spinner size="lg" /></Flex>
          ) : !enrollment ? (
            <Box py={4} textAlign="center" color="gray.500">No se encontró matrícula para este programa.</Box>
          ) : type === 'course' ? (
            <Box py={4} textAlign="center" color="gray.500">Los cursos no usan seguimiento de meses en este reporte.</Box>
          ) : (
            <>
              <Month
                idStudent={studentId}
                type={type}
                currentEnrollment={enrollment}
                program={{ id: programId, name: programName }}
                currentProgramType={programType}
                preselectedSemesterId={preselectedSemesterId}
                fetching={false}
                insert={false}
                onStateChange={setMonthState}
                setIsOpen={handleClose}
                showRequiredLabel
              />
              <Flex justify="flex-end" mt={4}>
                <Button
                  bg="#574feb"
                  color="white"
                  _hover={{ bg: '#473fdc' }}
                  onClick={handleUpdateMonths}
                  isLoading={updating}
                  isDisabled={updating || !monthState?.enrollmentId || (monthState?.isSemesterBased && !monthState?.semesterId)}
                  width="150px"
                >
                  Actualizar
                </Button>
              </Flex>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
